import { Fragment } from "react";
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { getPlatformLogoSrc } from "../../components/Reports/Components/utils";
import { numberFormatter } from "../../helper/Reports/common";
import { useAmazonLowPerformingSearchTerms } from "./dashboardHooks";
import SectionInfoIcon from "./SectionInfoIcon";

const userPlatform =
  JSON.parse(localStorage.getItem("user_platform")) || "amazon";

const METRIC_COLUMNS = [
  {
    key: "spends",
    title: "Spends",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
  },
  {
    key: "orders",
    title: "Orders",
    maximumFractionDigits: 0,
  },
];

const HEADER_CELL_SX = {
  borderBottom: "none",
  padding: "8px 12px",
  fontSize: "12px",
  color: "text.secondary",
  fontWeight: 600,
  bgcolor: "rgb(234, 230, 253)",
};

const LABEL_CELL_SX = {
  borderBottom: "1px solid #E0E0E0",
  padding: "6px 12px",
  fontSize: "12px",
  fontWeight: 600,
  bgcolor: "#f9f9f9",
};

const BODY_CELL_SX = {
  padding: "12px",
  fontSize: "12px",
};

function getFormattedCompChangePercent(value) {
  const formattedObj = {
    printableValue: "-",
    numericValue: value,
  };
  if (value === undefined || value === null || isNaN(value)) {
    return formattedObj;
  }
  if (value === 2147483647 || value === -2147483648) {
    formattedObj.printableValue = "∞";
    return formattedObj;
  }
  formattedObj.printableValue = `(${Number(value).toFixed(0)}%)`;
  return formattedObj;
}

function formatMetric(column, value) {
  if (value === null || value === undefined || value === "") return "-";
  return numberFormatter(
    value,
    !!column.isCurrencyFormat,
    column.format || "number",
    column.maximumFractionDigits
  );
}

function MetricHeader({ title }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        flexDirection: "column",
        justifyContent: "flex-end",
        gap: 0.5,
      }}
    >
      <img
        src={getPlatformLogoSrc(userPlatform)}
        alt={userPlatform}
        style={{ width: "14px", height: "14px" }}
      />
      <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>{title}</Typography>
    </Box>
  );
}

function CurrentMetricCell({ column, row }) {
  const value = row?.current_data?.[column.key];
  const changePercent = getFormattedCompChangePercent(
    row?.change_percentage_data?.[column.key]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-end"
    >
      <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
        {formatMetric(column, value)}
      </Typography>
      {changePercent?.printableValue && changePercent.printableValue !== "-" ? (
        <Typography
          sx={{
            fontSize: "12px",
            color: changePercent.numericValue < 0 ? "#d32f2f" : "#388e3c",
            lineHeight: 1.1,
          }}
        >
          {changePercent.printableValue}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * @param {object} props
 * @param {string[]} [props.selectedPortfolios]
 */
export default function LowPerformingSearchTerms({
  selectedPortfolios = [],
  sx,
} = {}) {
  const { rows, loading } = useAmazonLowPerformingSearchTerms(
    selectedPortfolios
  );

  const metricColSpan = METRIC_COLUMNS.length * 2;

  return (
    <Box sx={{ mb: 4, height: "100%", display: "flex", flexDirection: "column", ...sx }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: "16px" }}>
            Low Performing Search Terms
          </Typography>
          <SectionInfoIcon
            lines={[
              "Search terms with Spends greater than 0, for the selected dates and portfolios.",
              "Ranked by fewest Orders, then highest Spends. Shows 10 terms.",
              "Current is the selected date range. Previous is the immediately preceding range of the same length.",
              "Change % under Current = ((Current − Previous) / Previous) × 100.",
              "∞ is shown when Previous is 0.",
            ]}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Search terms with the fewest orders and highest spend versus the
          preceding period.
        </Typography>
      </Box>

      <Paper
        sx={{
          borderRadius: 2,
          boxShadow: "none",
          border: "1px solid #E0E0E0",
          overflow: "hidden",
          flex: 1,
        }}
      >
        <TableContainer>
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ ...HEADER_CELL_SX, width: "36%" }}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                    Search Terms
                  </Typography>
                </TableCell>
                {METRIC_COLUMNS.map((column) => (
                  <TableCell
                    key={column.key}
                    align="right"
                    colSpan={2}
                    sx={HEADER_CELL_SX}
                  >
                    <MetricHeader title={column.title} />
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={LABEL_CELL_SX} />
                {METRIC_COLUMNS.flatMap((column) => [
                  <TableCell
                    key={`${column.key}-current-label`}
                    align="right"
                    sx={LABEL_CELL_SX}
                  >
                    Current
                  </TableCell>,
                  <TableCell
                    key={`${column.key}-previous-label`}
                    align="right"
                    sx={{ ...LABEL_CELL_SX, color: "#00000099" }}
                  >
                    Previous
                  </TableCell>,
                ])}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? [...Array(10)].map((_, rowIndex) => (
                    <TableRow key={`skeleton-${rowIndex}`}>
                      <TableCell
                        sx={{
                          ...BODY_CELL_SX,
                          borderBottom:
                            rowIndex === 9 ? "none" : "1px solid #E0E0E0",
                        }}
                      >
                        <Skeleton width="80%" height={20} />
                      </TableCell>
                      {[...Array(metricColSpan)].map((_, cellIndex) => (
                        <TableCell
                          key={`skeleton-${rowIndex}-${cellIndex}`}
                          align="right"
                          sx={{
                            ...BODY_CELL_SX,
                            borderBottom:
                              rowIndex === 9 ? "none" : "1px solid #E0E0E0",
                          }}
                        >
                          <Skeleton
                            width="60px"
                            height={20}
                            sx={{ ml: "auto" }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : rows.length === 0
                  ? (
                    <TableRow>
                      <TableCell
                        colSpan={1 + metricColSpan}
                        align="center"
                        sx={{ ...BODY_CELL_SX, py: 4, borderBottom: "none" }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No low performing search terms for the selected period
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                  : rows.map((row, rowIndex) => (
                      <TableRow key={`${row?.search_term}-${rowIndex}`}>
                        <TableCell
                          sx={{
                            ...BODY_CELL_SX,
                            borderBottom:
                              rowIndex === rows.length - 1
                                ? "none"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "12px",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: 1.4,
                            }}
                          >
                            {row?.search_term || "-"}
                          </Typography>
                        </TableCell>
                        {METRIC_COLUMNS.map((column) => (
                          <Fragment key={column.key}>
                            <TableCell
                              align="right"
                              sx={{
                                ...BODY_CELL_SX,
                                borderBottom:
                                  rowIndex === rows.length - 1
                                    ? "none"
                                    : "1px solid #E0E0E0",
                              }}
                            >
                              <CurrentMetricCell column={column} row={row} />
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                ...BODY_CELL_SX,
                                color: "#00000099",
                                borderBottom:
                                  rowIndex === rows.length - 1
                                    ? "none"
                                    : "1px solid #E0E0E0",
                              }}
                            >
                              {formatMetric(
                                column,
                                row?.comparison_data?.[column.key]
                              )}
                            </TableCell>
                          </Fragment>
                        ))}
                      </TableRow>
                    ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
